import Phaser from 'phaser';

export class CharacterRenderer {
  private sceneRef: Phaser.Scene;
  private _playerGraphics!: Phaser.GameObjects.Graphics;
  private _playerTime = 0;
  private _usesSheet = false;
  private _lastWalkAt = 0;

  constructor(scene: Phaser.Scene) {
    this.sceneRef = scene;
  }

  /** Frame map de player-sheet-v2.png 1024×640 8×5×128 — R1C4-5 y R1C6-7 son salto/voltereta, no run */
  private static readonly PLAYER_FRAMES = {
    idle: [0, 1, 2, 1],                 // R0C0-2 ping-pong (GDD 4)
    walk: [4, 5, 6, 7, 8, 9, 10, 11],   // R0C4-7 + R1C0-3 (8f limpios, 9fps)
    run: [4, 5, 6, 7, 8, 9, 10, 11],    // mismo 8f a 14fps
    jump: [16, 16],                     // R2C0 brazos arriba
    djump: [14, 15],                    // R1C6-7 voltereta
    fall: [17, 17],                     // R2C1 plancha
    hurt: [26, 27],                     // R3C2-3
    throw: [20, 21, 20, 21],            // R2C4-5
    dead: [27, 32, 33, 32, 33, 27],
    sing: [35, 36, 35, 36, 35, 36, 35, 36],
  };

  private createPlayerAnims(): void {
    const F = CharacterRenderer.PLAYER_FRAMES;
    const defs: Array<[string, number[], number, boolean]> = [
      ['p-idle', F.idle, 8, true],
      ['p-walk', (F as unknown as { walk: number[] }).walk, 10, true],
      ['p-run', F.run, 16, true],
      ['p-jump', F.jump, 8, false],
      ['p-djump', F.djump, 12, false],
      ['p-fall', F.fall, 6, true],
      ['p-hurt', F.hurt, 12, false],
      ['p-dead', F.dead, 8, false],
      ['p-throw', F.throw, 12, false],
      ['p-sing', F.sing, 8, true],
    ];
    for (const [key, frames, frameRate, loop] of defs) {
      if (this.sceneRef.anims.exists(key)) continue;
      this.sceneRef.anims.create({
        key,
        frames: this.sceneRef.anims.generateFrameNumbers('player-sheet-v1', { frames }),
        frameRate,
        repeat: loop ? -1 : 0,
      });
    }
  }

  createPlayerSprite(x: number, y: number): Phaser.GameObjects.Sprite {
    if (this.sceneRef.textures.exists('player-sheet-v1')) {
      this._usesSheet = true;
      this.createPlayerAnims();
      const sprite = this.sceneRef.add.sprite(x, y, 'player-sheet-v1', 0);
      sprite.setOrigin(0.5, 118 / 128);
      sprite.setDisplaySize(140, 158); // 156×175 -10% (pide bajar)
      sprite.setDepth(1);
      sprite.play('p-idle');
      return sprite;
    }

    // Procedural fallback: invisible collider sprite + graphics drawing
    const sprite = this.sceneRef.add.sprite(x, y, 'player_idle');
    sprite.setVisible(false);
    sprite.setOrigin(0.5);
    sprite.setScale(1.2);

    // Create procedural graphics for the player
    const g = this.sceneRef.add.graphics();
    g.setScrollFactor(1);
    g.setDepth(1);
    (g as any)._baseWidth = 36;
    (g as any)._baseHeight = 56;

    this._playerGraphics = g;

    // Position graphics: centered on (x, y)
    g.x = x - (g as any)._baseWidth / 2;
    g.y = y - (g as any)._baseHeight / 2;
    g.clear();
    this.drawPlayer(g, 0, 'idle', 0, 0, false, false, 0, false, 0, 0);

    return sprite;
  }

  updatePlayer(playerSprite: Phaser.GameObjects.Sprite, state: {
    x: number; y: number; facing: 'left' | 'right';
    vx: number; vy: number;
    isHurt: boolean; isInvulnerable: boolean; invulnerabilityTimer: number;
    isDead: boolean; stoneCooldown: number; activeStones: number;
    hasDoubleJumped?: boolean; justThrew?: boolean; onGround?: boolean; isSprinting?: boolean;
  }): void {
    // === Spritesheet mode: sprite IS the visual ===
    if (this._usesSheet) {
      playerSprite.x = state.x;
      playerSprite.y = state.y; // feet position (origin 0.5,1)
      playerSprite.setFlipX(state.facing === 'left');

      const onGround = state.onGround ?? true;
      const now = this.sceneRef.time.now;
      const moving = Math.abs(state.vx) > 0.25 && onGround;
      if (moving) this._lastWalkAt = now;
      const recentlyMoving = now - this._lastWalkAt < 180;
      let anim = 'p-idle';
      if (state.isDead) anim = 'p-dead';
      else if (state.isHurt) anim = 'p-hurt';
      else if (state.justThrew) anim = 'p-throw';
      else if (state.hasDoubleJumped && state.vy < 0) anim = 'p-djump';
      else if (state.vy < -0.5 && !onGround) anim = 'p-jump';
      else if (state.vy > 1 && !onGround) anim = 'p-fall';
      else if (moving || (recentlyMoving && onGround)) anim = state.isSprinting ? 'p-run' : 'p-walk';

      const current = playerSprite.anims.currentAnim?.key;
      const isWalkRun = (a: string | undefined) => a === 'p-walk' || a === 'p-run';
      if (isWalkRun(current) && isWalkRun(anim)) {
        playerSprite.anims.timeScale = anim === 'p-run' ? 1 : 0.625; // 10/16
        if (current === anim) { /* keep */ } else {
          const curFrame = playerSprite.anims.currentFrame?.index ?? 0;
          playerSprite.play(anim, true);
          try { playerSprite.anims.setCurrentFrame(playerSprite.anims.currentAnim!.frames[curFrame % playerSprite.anims.currentAnim!.frames.length]); } catch { /* ignore */ }
        }
        playerSprite.anims.timeScale = anim === 'p-run' ? 1 : 0.625;
      } else if (current !== anim) {
        playerSprite.anims.timeScale = 1;
        const isOneShot = anim === 'p-hurt' || anim === 'p-throw' || anim === 'p-djump';
        if (isOneShot) {
          playerSprite.play(anim, true);
          playerSprite.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
            if (!playerSprite.anims) return;
            const cur = playerSprite.anims.currentAnim?.key;
            if (cur === anim) playerSprite.play('p-idle', true);
          });
        } else {
          playerSprite.play(anim, true);
        }
      } else {
        if (isWalkRun(anim)) playerSprite.anims.timeScale = anim === 'p-run' ? 1 : 0.625;
      }

      // Alpha for invulnerability blink
      if (state.isInvulnerable && !state.isHurt) {
        const visible = Math.floor(state.invulnerabilityTimer / 80) % 2 === 0;
        playerSprite.alpha = visible ? 1 : 0.3;
      } else {
        playerSprite.alpha = 1;
      }
      if (state.isHurt) playerSprite.setTint(0xff4444);
      else playerSprite.clearTint();
      return;
    }

    // === Procedural fallback mode ===
    // Get the graphics object
    const g = this._playerGraphics;
    if (!g) return;

    // Update sprite position (invisible, for camera follow) — center of hitbox
    playerSprite.x = state.x;
    playerSprite.y = state.y - (g as any)._baseHeight / 2;

    // Position graphics: bottom-centered on (state.x, state.y) where state.y is feet
    g.x = state.x - (g as any)._baseWidth / 2;
    g.y = state.y - (g as any)._baseHeight;
    g.clear();

    // Advance time con delta real para 60/120/144Hz
    const delta = (this.sceneRef.game.loop.delta as number) || 16;
    this._playerTime += delta;

    const onGround = (state as unknown as { onGround?: boolean }).onGround ?? true;
    const isSpr = (state as unknown as { isSprinting?: boolean }).isSprinting ?? false;
    const pState = state.isDead ? 'dead'
      : (state as unknown as { justThrew?: boolean }).justThrew ? 'throw'
      : (state as unknown as { hasDoubleJumped?: boolean }).hasDoubleJumped && state.vy < 0 ? 'djump'
      : state.vy < -0.5 && !onGround ? 'jump'
      : state.vy > 1 && !onGround ? 'fall'
      : Math.abs(state.vx) > 0.5 && onGround ? (isSpr ? 'run' : 'walk') : 'idle';
    // Procedural solo tiene run/jump/fall/dead/idle → mapear walk/throw/djump
    const drawState = pState === 'walk' ? 'run' : (pState === 'throw' || pState === 'djump' ? 'jump' : pState);
    this.drawPlayer(g, this._playerTime, drawState,
      state.vx, state.vy,
      state.isHurt, state.isInvulnerable, state.invulnerabilityTimer,
      state.isDead, state.stoneCooldown, state.activeStones
    );

    // Alpha for invulnerability
    if (state.isInvulnerable && !state.isHurt) {
      const visible = Math.floor(state.invulnerabilityTimer / 80) % 2 === 0;
      playerSprite.alpha = visible ? 1 : 0.3;
    } else {
      playerSprite.alpha = 1;
    }

    // Hurt flash (tint the sprite)
    if (state.isHurt) {
      playerSprite.setTint(0xff4444);
    } else {
      playerSprite.clearTint();
    }
  }

  // Helper: draw player procedurally with animations
  private drawPlayer(g: Phaser.GameObjects.Graphics, time: number, state: string,
    _vx: number, vy: number,
    _isHurt: boolean, _isInvulnerable: boolean, _invulnerabilityTimer: number,
    _isDead: boolean, _stoneCooldown: number, _activeStones: number): void {
    const cx = 18; // center x of 36px width
    const skinColor = 0x8B6F47; // mulato skin (#8B6F47)
    const shortsColor = 0x4682B4; // denim blue (#4682B4)
    const capColor = 0x000080; // navy blue (#000080)
    const chainColor = 0xFFD700; // gold (#FFD700)
    const shoeColor = 0x553322; // brown shoes

    if (_isDead) {
      // Dead: horizontal body
      // Body lying down
      (g as any).fillStyle(shortsColor);
      (g as any).fillRect(cx - 14, 20, 28, 8);
      // Head
      (g as any).fillStyle(skinColor);
      (g as any).fillCircle(cx + 14, 28, 8);
      // Cap (backwards)
      (g as any).fillStyle(capColor);
      (g as any).fillRect(cx + 7, 24, 14, 4);
      (g as any).fillRect(cx + 5, 20, 10, 5);
      // X eyes
      (g as any).lineStyle(1, 0xff0000);
      g.beginPath(); g.moveTo(cx + 11, 27); g.lineTo(cx + 13, 29); g.strokePath();
      g.beginPath(); g.moveTo(cx + 13, 27); g.lineTo(cx + 11, 29); g.strokePath();
      g.beginPath(); g.moveTo(cx + 15, 27); g.lineTo(cx + 17, 29); g.strokePath();
      g.beginPath(); g.moveTo(cx + 17, 27); g.lineTo(cx + 15, 29); g.strokePath();
      // Arms/legs
      (g as any).fillStyle(shortsColor);
      (g as any).fillRect(cx - 16, 24, 6, 5);
      (g as any).fillRect(cx + 12, 24, 6, 5);
      (g as any).fillStyle(0x334466);
      (g as any).fillRect(cx - 16, 30, 6, 5);
      (g as any).fillRect(cx + 12, 30, 6, 5);
      return;
    }

    // Standing character (idle, run, jump, fall)
    // Head (centered at cx, 12)
    (g as any).fillStyle(skinColor);
    (g as any).fillCircle(cx, 12, 8);

    // Hair (dark)
    (g as any).fillStyle(0x222222);
    (g as any).fillCircle(cx, 9, 7);
    (g as any).fillRect(cx - 7, 5, 14, 5);

    // Cap (backwards NY style - navy blue #000080)
    // Brim points forward (right side when facing right)
    (g as any).fillStyle(capColor);
    (g as any).fillRect(cx - 8, 4, 16, 4);
    (g as any).fillRect(cx - 6, 1, 12, 4);
    // NY logo (simplified)
    (g as any).fillStyle(0xffffff);
    (g as any).fillRect(cx - 1, 2, 2, 2);
    (g as any).fillRect(cx + 1, 2, 2, 2);

    // Eyes (confident expression)
    (g as any).fillStyle(0xffffff);
    (g as any).fillCircle(cx - 3, 11, 2);
    (g as any).fillCircle(cx + 3, 11, 2);
    (g as any).fillStyle(0x222222);
    (g as any).fillCircle(cx - 2, 11, 1);
    (g as any).fillCircle(cx + 4, 11, 1);

    // Mouth (confident smirk)
    (g as any).lineStyle(1, 0x333333);
    const smirkOffset = Math.sin(time * 0.003);
    g.beginPath(); g.moveTo(cx - 3, 15 + smirkOffset); g.lineTo(cx - 1, 16 + smirkOffset); g.lineTo(cx + 4, 14 + smirkOffset); g.strokePath();

    // Neck
    (g as any).fillStyle(skinColor);
    (g as any).fillRect(cx - 3, 18, 6, 4);

    // Torso: athletic bare torso (no shirt) — breathing offset on idle
    const torsoY = state === 'idle' ? 22 + Math.sin(time * 0.003) * 1.5 : 22;
    (g as any).fillStyle(skinColor);
    (g as any).fillRect(cx - 9, torsoY, 18, 16);
    // Pectoral line (athletic definition)
    (g as any).lineStyle(0.5, 0x7a5a3a);
    g.beginPath(); g.moveTo(cx - 4, 26); g.lineTo(cx + 4, 26); g.strokePath();
    // Abs lines
    g.beginPath(); g.moveTo(cx, 28); g.lineTo(cx, 36); g.strokePath();

    // Gold chain (#FFD700) around neck
    (g as any).lineStyle(1, chainColor);
    g.beginPath(); g.moveTo(cx - 4, 20); g.lineTo(cx, 24); g.lineTo(cx + 4, 20); g.strokePath();
    // Chain pendant
    (g as any).fillStyle(chainColor);
    (g as any).fillCircle(cx, 24, 1.5);

    // Arms (athletic, bare)
    (g as any).fillStyle(skinColor);
    (g as any).fillRect(cx - 13, 22, 5, 14);
    (g as any).fillRect(cx + 8, 22, 5, 14);
    // Hands
    (g as any).fillStyle(skinColor);
    (g as any).fillCircle(cx - 10, 37, 3);
    (g as any).fillCircle(cx + 10, 37, 3);

    // Shorts: denim blue (#4682B4)
    (g as any).fillStyle(shortsColor);
    (g as any).fillRect(cx - 10, 38, 20, 4);
    (g as any).lineStyle(1, 0x366294);
    (g as any).strokeRect(cx - 10, 38, 20, 4);

    // Legs (athletic, bare)
    (g as any).fillStyle(skinColor);
    (g as any).fillRect(cx - 8, 42, 7, 7);
    (g as any).fillRect(cx + 1, 42, 7, 7);

    // Shoes (brown)
    (g as any).fillStyle(shoeColor);
    (g as any).fillRect(cx - 9, 47, 8, 7);
    (g as any).fillRect(cx + 1, 47, 8, 7);

    // === ANIMATION STATE OVERRIDES ===

    if (state === 'run') {
      // Running: alternate leg positions based on animation cycle
      const cycle = Math.sin(time * 0.012);
      if (cycle > 0) {
        // Left leg forward, right leg back
        (g as any).fillStyle(skinColor);
        (g as any).fillRect(cx - 10, 42, 6, 5);
        (g as any).fillRect(cx + 3, 42, 6, 5);
        (g as any).fillStyle(shoeColor);
        (g as any).fillRect(cx - 11, 45, 8, 7);
        (g as any).fillRect(cx + 3, 45, 8, 7);
      } else {
        // Right leg forward, left leg back
        (g as any).fillStyle(skinColor);
        (g as any).fillRect(cx - 4, 42, 6, 5);
        (g as any).fillRect(cx - 8, 42, 6, 5);
        (g as any).fillStyle(shoeColor);
        (g as any).fillRect(cx + 3, 45, 8, 7);
        (g as any).fillRect(cx - 11, 45, 8, 7);
      }
    } else if (state === 'jump') {
      // Jumping: legs tucked when going up, extended when falling
      if (vy < -3) {
        // Going up: legs tucked
        (g as any).fillStyle(skinColor);
        (g as any).fillRect(cx - 9, 44, 6, 4);
        (g as any).fillRect(cx + 3, 44, 6, 4);
        (g as any).fillStyle(shoeColor);
        (g as any).fillRect(cx - 10, 46, 7, 5);
        (g as any).fillRect(cx + 2, 46, 7, 5);
      } else {
        // Falling: legs extended
        (g as any).fillStyle(skinColor);
        (g as any).fillRect(cx - 8, 42, 7, 7);
        (g as any).fillRect(cx + 1, 42, 7, 7);
        (g as any).fillStyle(shoeColor);
        (g as any).fillRect(cx - 9, 47, 8, 7);
        (g as any).fillRect(cx + 1, 47, 8, 7);
      }
    } else if (state === 'fall') {
      // Falling: legs extended downward
      (g as any).fillStyle(skinColor);
      (g as any).fillRect(cx - 8, 42, 7, 7);
      (g as any).fillRect(cx + 1, 42, 7, 7);
      (g as any).fillStyle(shoeColor);
      (g as any).fillRect(cx - 9, 47, 8, 7);
      (g as any).fillRect(cx + 1, 47, 8, 7);
    }
    // Idle: use the base standing pose with breathing (torso height already includes breathe offset)
  }

  // ============================================================
  // ENEMY RENDERING — procedural graphics with distinct identities
  // ============================================================

  createEnemySprite(x: number, y: number, type: string, spriteKey?: string): Phaser.GameObjects.Sprite {
    // Image-based enemy: texture enemy_<type> or per-spawn override drives the visuals (56×80)
    const imgKey = spriteKey ?? `enemy_${type}`;
    if (this.sceneRef.textures.exists(imgKey)) {
      const sprite = this.sceneRef.add.sprite(x, y, imgKey);
      sprite.setOrigin(0.5, 1); // bottom-centered on feet position
      sprite.setDisplaySize(95, 135); // +30% over previous 73×104
      sprite.setDepth(1);
      (sprite as any)._enemyType = type;
      (sprite as any)._enemyImageMode = true;
      return sprite;
    }

    // Create a transparent placeholder sprite for positioning
    const sprite = this.sceneRef.add.sprite(x, y, 'player_idle'); // dummy key, will be overridden
    sprite.setVisible(false); // hide the placeholder
    sprite.setOrigin(0.5);
    sprite.setScale(1.2);
    (sprite as any)._enemyType = type;

    // Create procedural graphics for this enemy type
    const charData = (this.sceneRef as any).charData?.[type] || { width: 40, height: 56 };
    const gw = charData.width || 40;
    const gh = charData.height || 56;

    const g = this.sceneRef.add.graphics();
    g.setScrollFactor(1);
    g.setDepth(1);
    g.setScale(1.3); // scale drawn content proportionally (drawEnemy uses hardcoded coords)
    (g as any)._enemyType = type;
    (g as any)._baseWidth = gw;
    (g as any)._baseHeight = Math.round(gh * 1.3); // compensate for scale so feet align with state.y

    // Store the graphics per-instance on the sprite so multiple enemies of the
    // same type do NOT share/render on top of each other.
    (sprite as any)._graphics = g;

    // Position graphics: bottom-centered (x = center, y = feet)
    g.x = x - (g as any)._baseWidth / 2;
    g.y = y - (g as any)._baseHeight;

    // Draw initial frame so the enemy is visible immediately
    g.clear();
    this.drawEnemy(g, type, { vx: 0, vy: 0, state: 'patrol' });

    return sprite;
  }

  updateEnemy(enemySprite: Phaser.GameObjects.Sprite, state: {
    x: number; y: number; facing: 'left' | 'right';
    state: string; // patrol, detect, chase, attack, hurt, dead
    vx: number; vy: number;
  }): void {
    // Update sprite position (for colliders, etc.)
    enemySprite.x = state.x;
    enemySprite.y = state.y;

    // Facing
    if (state.facing === 'left') {
      enemySprite.setFlipX(true);
    } else {
      enemySprite.setFlipX(false);
    }

    // Color coding by state — ONLY procedural mode (image sprites would look
    // wrong fully tinted green/red). Kept for parity with previous behavior.
    const g0 = (enemySprite as any)._graphics;
    if (g0) {
      const colors: Record<string, number> = {
        patrol: 0x44aa44,
        detect: 0xcccc44,
        chase: 0xff8844,
        attack: 0xff4444,
        hurt: 0xff0000,
        dead: 0x444444,
      };
      enemySprite.setTint(colors[state.state] || 0xcc4444);
    }

    // Get the per-instance graphics object stored on the sprite
    const g = (enemySprite as any)._graphics;
    if (!g) return;

    // Position graphics: bottom-centered (x = center, y = feet)
    g.x = state.x - (g as any)._baseWidth / 2;
    g.y = state.y - (g as any)._baseHeight;
    g.clear();

    // Draw the enemy procedurally based on type and state
    this.drawEnemy(g, (enemySprite as any)._enemyType || 'ujc', state);
  }

  // Helper: draw enemy procedurally based on type
  private drawEnemy(g: Phaser.GameObjects.Graphics, type: string, state: {
    vx: number; vy: number; state: string;
  }): void {
    const cx = (g as any)._baseWidth / 2;
    const wh = (g as any)._baseWidth;
    const bh = (g as any)._baseHeight;

    // Common enemy structure: head, body, legs, arms
    const skinColor = 0xd4a574; // mulato skin

    if (type === 'pcc') {
      // PCC: White guayabera (#FFFFFF), pens visible, dark pants, upright posture
      const bodyColor = 0xffffff;
      const pantsColor = 0x2a2a3a;
      const penColor = 0x111111;

      // Head
      (g as any).fillStyle(skinColor);
      (g as any).fillCircle(cx, 12, 8);
      // Hair (short, neat)
      (g as any).fillStyle(0x222222);
      (g as any).fillCircle(cx, 9, 7);
      (g as any).fillRect(cx - 7, 5, 14, 5);
      // Eyes (serious)
      (g as any).fillStyle(0xffffff);
      (g as any).fillCircle(cx - 3, 11, 2);
      (g as any).fillCircle(cx + 3, 11, 2);
      (g as any).fillStyle(0x222222);
      (g as any).fillCircle(cx - 2, 11, 1);
      (g as any).fillCircle(cx + 4, 11, 1);
      // Mouth (neutral line)
      (g as any).lineStyle(1, 0x333333);
      g.beginPath(); g.moveTo(cx - 3, 15); g.lineTo(cx + 3, 15); g.strokePath();

      // Neck
      (g as any).fillStyle(skinColor);
      (g as any).fillRect(cx - 3, 18, 6, 4);

      // Body: white guayabera with stitching lines
      (g as any).fillStyle(bodyColor);
      (g as any).fillRect(cx - 11, 22, 22, 18);
      (g as any).lineStyle(1, 0xcccccc);
      (g as any).strokeRect(cx - 11, 22, 22, 18);
      // Guayabera vertical folds
      g.beginPath(); g.moveTo(cx - 5, 26); g.lineTo(cx - 5, 38); g.strokePath();
      g.beginPath(); g.moveTo(cx + 5, 26); g.lineTo(cx + 5, 38); g.strokePath();
      // Buttons
      (g as any).fillStyle(0xddaa44);
      (g as any).fillCircle(cx, 28, 1.5);
      (g as any).fillCircle(cx, 33, 1.5);

      // Pens in chest pocket (distinctive detail)
      (g as any).fillStyle(penColor);
      (g as any).fillRect(cx - 1, 24, 1.5, 8);
      (g as any).fillRect(cx + 1.5, 25, 1.5, 7);
      // Pen clips
      (g as any).fillStyle(0x888888);
      (g as any).fillRect(cx - 1, 23, 1.5, 2);
      (g as any).fillRect(cx + 1.5, 24, 1.5, 2);

      // Arms
      (g as any).fillStyle(bodyColor);
      (g as any).fillRect(cx - 15, 23, 5, 14);
      (g as any).fillRect(cx + 10, 23, 5, 14);
      // Hands
      (g as any).fillStyle(skinColor);
      (g as any).fillCircle(cx - 12, 38, 3);
      (g as any).fillCircle(cx + 12, 38, 3);

      // Belt
      (g as any).fillStyle(0x333333);
      (g as any).fillRect(cx - 11, 38, 22, 3);
      (g as any).fillStyle(0xddaa44);
      (g as any).fillRect(cx - 2, 38, 4, 3);

      // Legs: dark pants
      (g as any).fillStyle(pantsColor);
      (g as any).fillRect(cx - 9, 41, 7, 10);
      (g as any).fillRect(cx + 2, 41, 7, 10);
      // Boots
      (g as any).fillStyle(0x333333);
      (g as any).fillRect(cx - 10, 49, 9, 5);
      (g as any).fillRect(cx + 1, 49, 9, 5);

    } else if (type === 'fmc') {
      // FMC: Pink clothes (#FF69B4), female silhouette, rollers/scarf
      const dressColor = 0xff69b4;
      const darkPink = 0xcc4488;

      // Head
      (g as any).fillStyle(skinColor);
      (g as any).fillCircle(cx, 12, 8);
      // Hair with rollers (curly afro-style)
      (g as any).fillStyle(0x1a1a1a);
      (g as any).fillCircle(cx, 9, 8);
      // Rollers (small circles on top)
      (g as any).fillStyle(0xff69b4);
      (g as any).fillCircle(cx - 5, 3, 3);
      (g as any).fillCircle(cx, 1, 3);
      (g as any).fillCircle(cx + 5, 3, 3);
      // Eyes (friendly)
      (g as any).fillStyle(0xffffff);
      (g as any).fillCircle(cx - 3, 11, 2);
      (g as any).fillCircle(cx + 3, 11, 2);
      (g as any).fillStyle(0x222222);
      (g as any).fillCircle(cx - 2, 11, 1);
      (g as any).fillCircle(cx + 4, 11, 1);
      // Smile
      (g as any).lineStyle(1, 0x333333);
      g.beginPath(); g.moveTo(cx - 3, 15); g.lineTo(cx + 3, 15); g.strokePath();

      // Neck
      (g as any).fillStyle(skinColor);
      (g as any).fillRect(cx - 3, 18, 6, 4);

      // Body: pink dress (fitted female silhouette)
      (g as any).fillStyle(dressColor);
      (g as any).fillRect(cx - 12, 22, 24, 18);
      (g as any).lineStyle(1, darkPink);
      (g as any).strokeRect(cx - 12, 22, 24, 18);
      // Dress detail (waist line)
      (g as any).lineStyle(1, darkPink);
      g.beginPath(); g.moveTo(cx - 10, 30); g.lineTo(cx + 10, 30); g.strokePath();

      // Arms
      (g as any).fillStyle(dressColor);
      (g as any).fillRect(cx - 16, 23, 5, 14);
      (g as any).fillRect(cx + 11, 23, 5, 14);
      // Hands
      (g as any).fillStyle(skinColor);
      (g as any).fillCircle(cx - 13, 38, 3);
      (g as any).fillCircle(cx + 13, 38, 3);

      // Skirt (flared)
      (g as any).fillStyle(dressColor);
      (g as any).fillRect(cx - 13, 38, 26, 4);
      (g as any).fillRect(cx - 14, 40, 28, 3);

      // Legs (bare)
      (g as any).fillStyle(skinColor);
      (g as any).fillRect(cx - 8, 41, 6, 8);
      (g as any).fillRect(cx + 2, 41, 6, 8);
      // Shoes (pink)
      (g as any).fillStyle(dressColor);
      (g as any).fillRect(cx - 9, 47, 8, 7);
      (g as any).fillRect(cx + 1, 47, 8, 7);

    } else if (type === 'ujc') {
      // UJC: Blue hoodie (#0000CD), hunched/aggressive, bat in hand
      const hoodieColor = 0x0000cd;
      const darkHoodie = 0x000088;

      // Head (slightly hunched - lower position)
      (g as any).fillStyle(skinColor);
      (g as any).fillCircle(cx, 14, 8);
      // Hair (messy)
      (g as any).fillStyle(0x1a1a1a);
      (g as any).fillCircle(cx, 11, 7);
      (g as any).fillRect(cx - 7, 7, 14, 5);
      // Eyes (aggressive - angled)
      (g as any).fillStyle(0xffffff);
      (g as any).fillCircle(cx - 3, 13, 2);
      (g as any).fillCircle(cx + 3, 13, 2);
      (g as any).fillStyle(0xff2222); // red angry eyes
      (g as any).fillCircle(cx - 2, 13, 1);
      (g as any).fillCircle(cx + 4, 13, 1);
      // Angry mouth (downward curve)
      (g as any).lineStyle(1, 0x333333);
      g.beginPath(); g.moveTo(cx - 3, 17); g.lineTo(cx + 3, 16); g.strokePath();

      // Neck (hunched)
      (g as any).fillStyle(skinColor);
      (g as any).fillRect(cx - 3, 18, 6, 4);

      // Body: blue hoodie (baggy)
      (g as any).fillStyle(hoodieColor);
      (g as any).fillRect(cx - 12, 22, 24, 18);
      (g as any).lineStyle(1, darkHoodie);
      (g as any).strokeRect(cx - 12, 22, 24, 18);
      // Hoodie pocket
      (g as any).fillStyle(darkHoodie, 0.5);
      (g as any).fillRect(cx - 6, 32, 12, 6);

      // Arms
      (g as any).fillStyle(hoodieColor);
      (g as any).fillRect(cx - 16, 23, 5, 14);
      (g as any).fillRect(cx + 11, 23, 5, 14);
      // Hands
      (g as any).fillStyle(skinColor);
      (g as any).fillCircle(cx - 13, 38, 3);
      (g as any).fillCircle(cx + 13, 38, 3);

      // Bat in right hand (brown line)
      (g as any).fillStyle(0x8B4513);
      (g as any).fillRect(cx + 13, 28, 2, 14);
      // Bat handle wrap
      (g as any).fillStyle(0x553322);
      (g as any).fillRect(cx + 12, 36, 4, 3);

      // Belt
      (g as any).fillStyle(0x333333);
      (g as any).fillRect(cx - 11, 38, 22, 3);

      // Legs: dark jeans
      (g as any).fillStyle(0x2a2a4a);
      (g as any).fillRect(cx - 9, 41, 7, 10);
      (g as any).fillRect(cx + 2, 41, 7, 10);
      // Boots
      (g as any).fillStyle(0x333333);
      (g as any).fillRect(cx - 10, 49, 9, 5);
      (g as any).fillRect(cx + 1, 49, 9, 5);

    } else {
      // Default enemy: generic
      (g as any).fillStyle(skinColor);
      (g as any).fillCircle(cx, 12, 8);
      (g as any).fillStyle(0x222222);
      (g as any).fillCircle(cx, 9, 7);
      (g as any).fillRect(cx - 7, 5, 14, 5);
      (g as any).fillStyle(0xffffff);
      (g as any).fillCircle(cx - 3, 11, 2);
      (g as any).fillCircle(cx + 3, 11, 2);
      (g as any).fillStyle(0x222222);
      (g as any).fillCircle(cx - 2, 11, 1);
      (g as any).fillCircle(cx + 4, 11, 1);
      (g as any).lineStyle(1, 0x333333);
      g.beginPath(); g.moveTo(cx - 3, 15); g.lineTo(cx + 3, 15); g.strokePath();
      (g as any).fillStyle(skinColor);
      (g as any).fillRect(cx - 3, 18, 6, 4);
      (g as any).fillStyle(0x445544);
      (g as any).fillRect(cx - 11, 22, 22, 18);
      (g as any).lineStyle(1, 0x334433);
      (g as any).strokeRect(cx - 11, 22, 22, 18);
      (g as any).fillStyle(0x445544);
      (g as any).fillRect(cx - 15, 23, 5, 14);
      (g as any).fillRect(cx + 10, 23, 5, 14);
      (g as any).fillStyle(skinColor);
      (g as any).fillCircle(cx - 12, 38, 3);
      (g as any).fillCircle(cx + 12, 38, 3);
      (g as any).fillStyle(0x333333);
      (g as any).fillRect(cx - 11, 38, 22, 3);
      (g as any).fillStyle(0x445544);
      (g as any).fillRect(cx - 9, 41, 7, 10);
      (g as any).fillRect(cx + 2, 41, 7, 10);
      (g as any).fillStyle(0x333333);
      (g as any).fillRect(cx - 10, 49, 9, 5);
      (g as any).fillRect(cx + 1, 49, 9, 5);
    }

    // Draw state-specific overlays
    if (state.state === 'dead') {
      // X eyes for dead enemies
      (g as any).lineStyle(2, 0xff0000);
      g.beginPath(); g.moveTo(cx - 4, 10); g.lineTo(cx - 2, 12); g.strokePath();
      g.beginPath(); g.moveTo(cx - 2, 10); g.lineTo(cx - 4, 12); g.strokePath();
      g.beginPath(); g.moveTo(cx + 2, 10); g.lineTo(cx + 4, 12); g.strokePath();
      g.beginPath(); g.moveTo(cx + 4, 10); g.lineTo(cx + 2, 12); g.strokePath();
    } else if (state.state === 'hurt') {
      // Flash red
      (g as any).fillStyle(0xff4444, 0.3);
      (g as any).fillRect(0, 0, wh, bh);
    }
  }

  // ============================================================
  // NPC RENDERING — procedural graphics with distinct identities
  // ============================================================

  renderNpc(type: string, x: number, y: number, interactable: boolean, spriteKey?: string): Phaser.GameObjects.Sprite {
    // Image-based NPC: texture npc_<type> or per-spawn override drives the visuals (65×104)
    const imgKey = spriteKey ?? `npc_${type}`;
    if (this.sceneRef.textures.exists(imgKey)) {
      const sprite = this.sceneRef.add.sprite(x, y, imgKey);
      sprite.setOrigin(0.5, 1); // bottom-centered on feet position
      sprite.setDisplaySize(85, 135); // +30% over previous 65×104
      sprite.setDepth(1);
      sprite.setScale(sprite.scaleX, sprite.scaleY); // keep aspect from displaySize
      (sprite as any)._npcType = type;
      (sprite as any)._npcImageMode = true;
      if (interactable) {
        const isMobile = (window as any).innerWidth < 768;
        const label = this.sceneRef.add.text(
          x,
          y - 110,
          isMobile ? 'TOCA' : '[E]',
          { fontSize: '10px', fontFamily: 'monospace', color: '#ffff00' }
        ).setOrigin(0.5);
        (sprite as any)._indicator = label;
      }
      return sprite;
    }

    // Create a placeholder sprite for interaction detection (invisible —
    // only the procedural graphics below render, hiding the player_idle frame).
    const sprite = this.sceneRef.add.sprite(x, y, 'player_idle');
    sprite.setVisible(false);
    sprite.setOrigin(0.5);
    sprite.setScale(1.2);
    (sprite as any)._npcType = type;

    // Create procedural graphics for this NPC type
    const charData = (this.sceneRef as any).charData?.[type] || { width: 36, height: 56 };
    const nw = charData.width || 36;
    const nh = charData.height || 56;

    const g = this.sceneRef.add.graphics();
    g.setScrollFactor(1);
    g.setDepth(1);
    g.setScale(1.3); // scale drawn content proportionally
    (g as any)._npcType = type;
    (g as any)._baseWidth = nw;
    (g as any)._baseHeight = Math.round(nh * 1.3); // compensate for scale so feet align with y
    (g as any)._interactable = interactable;

    // Store the graphics per-instance on the sprite (NPCs of the same type must
    // NOT share/render on top of each other).
    (sprite as any)._graphics = g;

    // Position graphics: bottom-centered (x = center, y = feet)
    g.x = x - (g as any)._baseWidth / 2;
    g.y = y - (g as any)._baseHeight;
    g.clear();
    this.drawNpc(g, type, 0);

    if (interactable) {
      const isMobile = (window as any).innerWidth < 768;
      const label = this.sceneRef.add.text(
        x,
        y - 35,
        isMobile ? 'TOCA' : '[E]',
        {
          fontSize: '10px',
          fontFamily: 'monospace',
          color: '#ffff00',
        }
      ).setOrigin(0.5);
      (sprite as any)._indicator = label;
    }

    return sprite;
  }

  /** Dead NPC: stays on screen lying on the ground (rotated 90°). Idempotent. */
  killNpc(npcSprite: Phaser.GameObjects.Sprite): void {
    if ((npcSprite as any)._killed) return;
    (npcSprite as any)._killed = true;
    const dir = npcSprite.flipX ? -1 : 1;
    npcSprite.setRotation(dir * Math.PI / 2);
    const indicator = (npcSprite as any)._indicator as Phaser.GameObjects.Text | undefined;
    if (indicator) indicator.setText('');
  }

  // Update NPC graphics position and animation
  updateNpcGraphics(_npcSprite: Phaser.GameObjects.Sprite, x: number, y: number, type: string): void {
    // Image-based NPC mode: position sprite and flip by movement direction
    if ((_npcSprite as any)._npcImageMode) {
      if ((_npcSprite as any)._killed) {
        // Keep the corpse at its last position (rotation already applied)
        return;
      }
      const lastX = (_npcSprite as any)._lastX ?? x;
      if (x < lastX - 0.01) _npcSprite.setFlipX(true);
      else if (x > lastX + 0.01) _npcSprite.setFlipX(false);
      (_npcSprite as any)._lastX = x;
      _npcSprite.x = x;
      _npcSprite.y = y; // origin (0.5, 1) → feet position
      return;
    }

    // Use the per-instance graphics stored on the sprite
    const g = (_npcSprite as any)._graphics;
    if (!g) return;

    (g as any)._animationTime = ((g as any)._animationTime || 0) + 16;

    // Position graphics: bottom-centered (x = center, y = feet)
    g.x = x - (g as any)._baseWidth / 2;
    g.y = y - (g as any)._baseHeight;
    g.clear();
    this.drawNpc(g, type, (g as any)._animationTime);
  }

  // Helper: draw NPC procedurally based on type
  private drawNpc(g: Phaser.GameObjects.Graphics, type: string, time: number): void {
    const cx = (g as any)._baseWidth / 2;
    const skinColor = 0xd4a574;

    if (type === 'tourist') {
      // Tourist: Hawaiian/colorful shirt, shorts, camera
      // Head
      (g as any).fillStyle(skinColor);
      (g as any).fillCircle(cx, 12, 8);
      // Sun hat
      (g as any).fillStyle(0xffaa44);
      (g as any).fillRect(cx - 10, 4, 20, 4);
      (g as any).fillRect(cx - 5, 0, 10, 5);
      // Eyes (happy)
      (g as any).fillStyle(0xffffff);
      (g as any).fillCircle(cx - 3, 11, 2);
      (g as any).fillCircle(cx + 3, 11, 2);
      (g as any).fillStyle(0x222222);
      (g as any).fillCircle(cx - 2, 11, 1);
      (g as any).fillCircle(cx + 4, 11, 1);
      // Smile
      (g as any).lineStyle(1, 0x333333);
      g.beginPath(); g.moveTo(cx - 3, 15); g.lineTo(cx + 3, 15); g.strokePath();

      // Neck
      (g as any).fillStyle(skinColor);
      (g as any).fillRect(cx - 3, 18, 6, 4);

      // Body: colorful Hawaiian shirt
      (g as any).fillStyle(0xff8844);
      (g as any).fillRect(cx - 10, 22, 20, 18);
      (g as any).lineStyle(1, 0xcc7733);
      (g as any).strokeRect(cx - 10, 22, 20, 18);
      // Hawaiian pattern (simple flowers)
      (g as any).fillStyle(0xffdd44);
      (g as any).fillCircle(cx - 4, 28, 2);
      (g as any).fillCircle(cx + 4, 32, 2);
      (g as any).fillStyle(0xff4444);
      (g as any).fillCircle(cx, 35, 1.5);

      // Camera around neck
      (g as any).fillStyle(0x333333);
      (g as any).fillCircle(cx, 26, 3);
      (g as any).fillStyle(0x555555);
      (g as any).fillCircle(cx, 26, 1.5);

      // Arms
      (g as any).fillStyle(0xff8844);
      (g as any).fillRect(cx - 14, 23, 5, 14);
      (g as any).fillRect(cx + 9, 23, 5, 14);
      // Hands
      (g as any).fillStyle(skinColor);
      (g as any).fillCircle(cx - 11, 38, 3);
      (g as any).fillCircle(cx + 11, 38, 3);

      // Shorts
      (g as any).fillStyle(0x556677);
      (g as any).fillRect(cx - 8, 41, 7, 10);
      (g as any).fillRect(cx + 1, 41, 7, 10);
      // Shoes
      (g as any).fillStyle(0x553322);
      (g as any).fillRect(cx - 9, 49, 8, 5);
      (g as any).fillRect(cx + 1, 49, 8, 5);

    } else if (type === 'pioneer') {
      // Pioneer: White school uniform, red neckerchief, smaller (child)
      const pCx = cx;

      // Head
      (g as any).fillStyle(skinColor);
      (g as any).fillCircle(pCx, 10, 7);
      // Hair (short, neat)
      (g as any).fillStyle(0x222222);
      (g as any).fillCircle(pCx, 8, 6);
      (g as any).fillRect(pCx - 6, 4, 12, 5);
      // Eyes (innocent)
      (g as any).fillStyle(0xffffff);
      (g as any).fillCircle(pCx - 2, 9, 2);
      (g as any).fillCircle(pCx + 2, 9, 2);
      (g as any).fillStyle(0x222222);
      (g as any).fillCircle(pCx - 1, 9, 1);
      (g as any).fillCircle(pCx + 3, 9, 1);
      // Smile
      (g as any).lineStyle(1, 0x333333);
      g.beginPath(); g.moveTo(pCx - 2, 13); g.lineTo(pCx + 2, 13); g.strokePath();

      // Neck
      (g as any).fillStyle(skinColor);
      (g as any).fillRect(pCx - 2, 15, 5, 3);

      // Red neckerchief (distinctive detail)
      (g as any).fillStyle(0xcc2222);
      (g as any).fillRect(pCx - 4, 16, 8, 3);
      (g as any).fillRect(pCx - 1, 18, 3, 5);

      // Body: white school uniform
      (g as any).fillStyle(0xffffff);
      (g as any).fillRect(pCx - 8, 20, 16, 14);
      (g as any).lineStyle(1, 0xcccccc);
      (g as any).strokeRect(pCx - 8, 20, 16, 14);

      // Arms
      (g as any).fillStyle(0xffffff);
      (g as any).fillRect(pCx - 11, 21, 4, 10);
      (g as any).fillRect(pCx + 7, 21, 4, 10);
      // Hands
      (g as any).fillStyle(skinColor);
      (g as any).fillCircle(pCx - 9, 32, 2.5);
      (g as any).fillCircle(pCx + 9, 32, 2.5);

      // Shorts
      (g as any).fillStyle(0x334466);
      (g as any).fillRect(pCx - 6, 33, 5, 8);
      (g as any).fillRect(pCx + 1, 33, 5, 8);
      // Shoes
      (g as any).fillStyle(0x333333);
      (g as any).fillRect(pCx - 7, 39, 6, 5);
      (g as any).fillRect(pCx, 39, 6, 5);

    } else {
      // Civilian: simple clothes
      // Head (slight breathing offset)
      const headY = 12 + Math.sin(time * 0.002) * 0.5;
      (g as any).fillStyle(skinColor);
      (g as any).fillCircle(cx, headY, 8);
      // Head
      (g as any).fillStyle(skinColor);
      (g as any).fillCircle(cx, 12, 8);
      // Hair
      (g as any).fillStyle(0x443322);
      (g as any).fillCircle(cx, 9, 7);
      (g as any).fillRect(cx - 7, 5, 14, 5);
      // Eyes
      (g as any).fillStyle(0xffffff);
      (g as any).fillCircle(cx - 3, 11, 2);
      (g as any).fillCircle(cx + 3, 11, 2);
      (g as any).fillStyle(0x222222);
      (g as any).fillCircle(cx - 2, 11, 1);
      (g as any).fillCircle(cx + 4, 11, 1);
      // Mouth (neutral)
      (g as any).lineStyle(1, 0x333333);
      g.beginPath(); g.moveTo(cx - 3, 15); g.lineTo(cx + 3, 15); g.strokePath();

      // Neck
      (g as any).fillStyle(skinColor);
      (g as any).fillRect(cx - 3, 18, 6, 4);

      // Body: simple shirt — light blue (distinct from the player's skin/denim)
      (g as any).fillStyle(0x5b8fd4);
      (g as any).fillRect(cx - 10, 22, 20, 18);
      (g as any).lineStyle(1, 0x41659f);
      (g as any).strokeRect(cx - 10, 22, 20, 18);

      // Arms
      (g as any).fillStyle(0x5b8fd4);
      (g as any).fillRect(cx - 14, 23, 5, 14);
      (g as any).fillRect(cx + 9, 23, 5, 14);
      // Hands
      (g as any).fillStyle(skinColor);
      (g as any).fillCircle(cx - 11, 38, 3);
      (g as any).fillCircle(cx + 11, 38, 3);

      // Pants (dark charcoal)
      (g as any).fillStyle(0x3a4055);
      (g as any).fillRect(cx - 8, 41, 7, 10);
      (g as any).fillRect(cx + 1, 41, 7, 10);
      // Shoes
      (g as any).fillStyle(0x2a2a35);
      (g as any).fillRect(cx - 9, 49, 8, 5);
      (g as any).fillRect(cx + 1, 49, 8, 5);
    }
  }

  updateNpcIndicator(npcSprite: Phaser.GameObjects.Sprite, inRange: boolean): void {
    const indicator = (npcSprite as any)._indicator as Phaser.GameObjects.Text | undefined;
    if (indicator) {
      // Follow the NPC in world space (image NPCs: origin bottom-center; procedural: offset -35)
      const offsetY = (npcSprite as any)._npcImageMode ? -110 : -35;
      indicator.setPosition(npcSprite.x, npcSprite.y + offsetY);
      indicator.setVisible(inRange);
    }
  }
}
